/**
 * Industry-Specific Opportunities Configuration
 * ================================================
 * Provides relevant, industry-specific opportunity suggestions
 * Each opportunity includes data sources for transparency
 */

const INDUSTRY_OPPORTUNITIES = {
  hospitality: [
    {
      title: 'Table Turnover Optimisation',
      description: 'Analysis shows your average table turn is 1.8x vs industry benchmark of 2.5x during peak dinner service.',
      reasoning: 'Reducing average dining time by 12 minutes through streamlined service flow could add 15 additional covers per night.',
      impact: '+$3,200/week potential',
      priority: 'high',
      suggestedActions: ['Review service timing from order to delivery', 'Implement pre-ordering for large parties', 'Optimise kitchen flow during 6-8pm peak'],
      sources: ['square', 'xero'],
      dataSource: 'POS transaction timestamps + reservation system data',
      benchmarkSource: 'IBISWorld Hospitality Industry Report'
    },
    {
      title: 'Wine & Beverage Upsell Opportunity',
      description: 'Only 38% of tables order beverages beyond water. Industry average for fine dining is 72%.',
      reasoning: 'Training staff on wine pairing recommendations could increase beverage attachment rate by 20%.',
      impact: '+$1,800/week potential',
      priority: 'high',
      suggestedActions: ['Implement wine pairing training program', 'Create beverage pairing menu cards', 'Track server-level beverage attachment rates'],
      sources: ['square', 'deputy'],
      dataSource: 'POS itemised sales data + staff rostering',
      benchmarkSource: 'ATO Hospitality Benchmarks'
    },
    {
      title: 'Food Cost Reduction via Supplier Review',
      description: 'Current food cost at 32% is above the 28% industry benchmark for your venue type.',
      reasoning: 'Supplier price comparison indicates 3 key ingredient categories are 15% above market average.',
      impact: '-$2,100/week in COGS savings',
      priority: 'medium',
      suggestedActions: ['Request competitive quotes from 2 alternative suppliers', 'Review portion sizes for top 10 dishes', 'Implement weekly wastage tracking by section'],
      sources: ['xero', 'square'],
      dataSource: 'Xero purchase ledger + POS sales mix analysis',
      benchmarkSource: 'ATO Small Business Benchmarks'
    },
    {
      title: 'Labour Cost Optimisation',
      description: 'Tuesday and Wednesday labour costs are 38% of revenue vs 30% target. Covers drop 40% on these days.',
      reasoning: 'Adjusting rosters to match demand patterns could reduce weekly labour spend by $1,200 without impacting service.',
      impact: '-$1,200/week in labour savings',
      priority: 'medium',
      suggestedActions: ['Cross-train staff for multi-role flexibility', 'Implement split shifts on quiet days', 'Review roster vs daily cover forecasts'],
      sources: ['deputy', 'xero'],
      dataSource: 'Deputy roster data + Xero payroll + POS daily revenue',
      benchmarkSource: 'Fair Work Hospitality Award + ATO benchmarks'
    }
  ],

  retail: [
    {
      title: 'Conversion Rate Improvement',
      description: 'Your foot traffic to sale conversion is 18% vs the 25% industry average for your store type.',
      reasoning: 'Analysis shows peak traffic hours (12-2pm) have the lowest conversion, suggesting staffing gaps.',
      impact: '+$4,200/week potential',
      priority: 'high',
      suggestedActions: ['Increase floor staff during 12-2pm peak', 'Review store layout and product placement', 'Implement greeting protocol for all customers'],
      sources: ['square', 'google_analytics'],
      dataSource: 'POS transaction data + foot traffic counter',
      benchmarkSource: 'NRA Retail Benchmarking Report'
    },
    {
      title: 'Average Transaction Value Increase',
      description: 'Current ATV of $62 is below the $85 benchmark for similar retailers.',
      reasoning: 'Upsell training and bundling promotions could lift ATV by 15-20%.',
      impact: '+$2,800/week potential',
      priority: 'medium',
      suggestedActions: ['Implement cross-selling recommendations at POS', 'Create bundle offers for complementary products', 'Train staff on suggestive selling techniques'],
      sources: ['square'],
      dataSource: 'POS itemised transaction data',
      benchmarkSource: 'ABS Retail Trade statistics'
    }
  ],

  healthcare: [
    {
      title: 'Appointment Fill Rate Improvement',
      description: 'Current fill rate of 72% is below the 85% benchmark. Cancellations account for 18% of booked slots.',
      reasoning: 'Implementing automated SMS reminders and waitlist management could recover 60% of cancellation gaps.',
      impact: '+$5,600/week potential',
      priority: 'high',
      suggestedActions: ['Set up automated 24hr SMS reminders', 'Implement online waitlist for cancelled slots', 'Review cancellation policy and enforce no-show fees'],
      sources: ['cliniko', 'xero'],
      dataSource: 'Practice management system booking data',
      benchmarkSource: 'AHPRA Health Industry Benchmarks'
    },
    {
      title: 'Rebooking Rate Enhancement',
      description: 'Only 45% of patients rebook at point of care vs 75% industry best practice.',
      reasoning: 'Receptionist-led rebooking at checkout could increase retention and reduce marketing costs.',
      impact: '+$3,200/week potential',
      priority: 'high',
      suggestedActions: ['Train reception on rebooking conversation', 'Implement recall system for chronic care plans', 'Set up automated recall reminders'],
      sources: ['cliniko'],
      dataSource: 'Patient rebooking data from practice management system',
      benchmarkSource: 'Health Industry Group benchmarks'
    }
  ],

  accounting: [
    {
      title: 'WIP Recovery Improvement',
      description: 'Lock-up days at 52 are above the 38-day industry benchmark. $285K in aged WIP identified.',
      reasoning: 'Faster billing cycles and progressive billing for large engagements could improve cash flow by 35%.',
      impact: '+$12,000/month in recovered WIP',
      priority: 'high',
      suggestedActions: ['Implement monthly progressive billing for engagements over $5K', 'Set up automated WIP aging alerts at 30/60/90 days', 'Review pricing on fixed-fee engagements vs actual hours'],
      sources: ['xero', 'practice_management'],
      dataSource: 'Practice management WIP data + Xero billing',
      benchmarkSource: 'IPA / CPA Australia Practice Benchmarking'
    },
    {
      title: 'Advisory Revenue Growth',
      description: 'Advisory revenue is 12% of total fees vs 25% industry benchmark for practices your size.',
      reasoning: 'Converting compliance-only clients to advisory clients increases average fee by 3x.',
      impact: '+$8,500/month potential',
      priority: 'medium',
      suggestedActions: ['Identify top 20 compliance clients for advisory cross-sell', 'Develop advisory service packages (CFO-as-a-Service)', 'Train managers on advisory conversation skills'],
      sources: ['xero', 'practice_management'],
      dataSource: 'Fee analysis by service type from practice management',
      benchmarkSource: 'IPA Practice Management Benchmarks'
    }
  ],

  legal: [
    {
      title: 'Recovery Rate Enhancement',
      description: 'Firm recovery rate at 82% is below the 90% benchmark. $180K in unrecovered fees identified.',
      reasoning: 'Better matter scoping and time capture discipline could improve recovery by 8 percentage points.',
      impact: '+$14,400/month in recovered fees',
      priority: 'high',
      suggestedActions: ['Implement matter budget approval workflow', 'Set up real-time time capture reminders', 'Review write-off patterns by matter type and fee earner'],
      sources: ['practice_management', 'xero'],
      dataSource: 'Practice management billing data + time entries',
      benchmarkSource: 'Law Society / Legal Industry benchmarks'
    },
    {
      title: 'Debtor Days Reduction',
      description: 'Average debtor days at 68 is above the 45-day industry benchmark.',
      reasoning: 'Automated debtor follow-up and upfront cost agreements could reduce collection time by 35%.',
      impact: '+$22,000/month in accelerated collections',
      priority: 'high',
      suggestedActions: ['Implement automated debtor reminders at 14/30/60 days', 'Require upfront cost agreements for new matters', 'Review billing frequency for long-running matters'],
      sources: ['xero', 'practice_management'],
      dataSource: 'Xero aged debtors report + billing system',
      benchmarkSource: 'Law Council of Australia benchmarks'
    }
  ],

  property: [
    {
      title: 'Vacancy Rate Reduction',
      description: 'Portfolio vacancy rate of 4.2% is above the 2.8% market average for your area.',
      reasoning: 'Faster tenant placement and proactive lease renewal conversations could reduce vacancy days by 40%.',
      impact: '+$6,800/month in reduced vacancy',
      priority: 'high',
      suggestedActions: ['Begin renewal conversations 90 days before lease expiry', 'Reduce listing-to-lease time with pre-qualification', 'Review rental pricing vs market comparables'],
      sources: ['property_management', 'rea'],
      dataSource: 'Property management system + REA market data',
      benchmarkSource: 'REIA Property Management benchmarks'
    },
    {
      title: 'Arrears Management',
      description: 'Rent arrears at 3.8% of collected rent is above the 2% industry benchmark.',
      reasoning: 'Automated payment reminders and early intervention could reduce arrears by 50%.',
      impact: '+$4,200/month in recovered rent',
      priority: 'medium',
      suggestedActions: ['Set up automated payment reminder SMS', 'Implement early intervention at day 3 of arrears', 'Review tenant screening criteria'],
      sources: ['property_management', 'xero'],
      dataSource: 'Trust accounting data + property management system',
      benchmarkSource: 'REIA state benchmarks'
    }
  ],

  professional: [
    {
      title: 'Utilisation Rate Improvement',
      description: 'Current utilisation at 62% is below the 75% benchmark for consulting firms.',
      reasoning: 'Reducing non-billable admin time and improving resource planning could add significant billable capacity.',
      impact: '+$18,000/month in additional billings',
      priority: 'high',
      suggestedActions: ['Audit non-billable time categories', 'Implement resource planning tool', 'Delegate admin tasks to support staff'],
      sources: ['practice_management', 'xero'],
      dataSource: 'Time tracking system + project management',
      benchmarkSource: 'Management Consulting Association benchmarks'
    },
    {
      title: 'Pipeline Conversion Improvement',
      description: 'Proposal win rate at 25% is below the 35% benchmark for firms your size.',
      reasoning: 'Improving proposal quality and follow-up process could increase win rate by 40%.',
      impact: '+$22,000/month in new project wins',
      priority: 'medium',
      suggestedActions: ['Implement proposal review process', 'Track and analyse win/loss reasons', 'Develop reusable proposal templates by service line'],
      sources: ['crm', 'practice_management'],
      dataSource: 'CRM pipeline data + proposal tracking',
      benchmarkSource: 'Management Consulting benchmarks'
    }
  ],

  // Default opportunities for other industries
  qsr: [
    {
      title: 'Drive-Through Speed Optimisation',
      description: 'Average DT wait time at Footscray is 4.8 min vs network target of 3.5 min. Penrith and Southport consistently beat target.',
      reasoning: 'Reducing Footscray DT wait by 1.3 min could lift throughput by ~18 cars/hr during peak, adding $2,400/day in revenue.',
      impact: '+$2,400/day potential at Footscray',
      priority: 'high',
      suggestedActions: ['Deploy dual-lane ordering at Footscray', 'Adopt Southport\'s pre-staging workflow', 'Add dedicated DT expeditor during 11am–1pm'],
      sources: ['summit_qsr', 'square'],
      dataSource: 'DT throughput sensors + POS transaction timestamps',
      benchmarkSource: 'QSR Media Benchmark Report',
      likelihood: 85,
      runway: '4–6 weeks',
      schedule: 'Week 1: Audit Footscray DT flow. Week 2-3: Install dual-lane hardware. Week 4-5: Staff training on pre-staging. Week 6: Go live + monitor.',
      bestChance: 'Start with the pre-staging workflow (zero capital cost) — this alone delivered a 0.6 min reduction at Southport. Combine with shift-dedicated DT expeditor before committing to dual-lane hardware investment.',
    },
    {
      title: 'Labour Efficiency — Prospect Below SPLH Target',
      description: 'Prospect is running $55.40 SPLH against a $65 target — lowest in the network. Labour cost at 27.8% vs 25% benchmark.',
      reasoning: 'Reallocating 12 hours/week from low-traffic Tuesday shifts to peak Friday/Saturday could lift SPLH by $8+.',
      impact: '+$9.60 SPLH uplift potential',
      priority: 'high',
      suggestedActions: ['Audit Prospect shift roster vs hourly sales data', 'Reduce Tuesday/Wednesday FTEs by 2', 'Cross-train for multi-station coverage'],
      sources: ['summit_qsr', 'tanda'],
      dataSource: 'Rostering data + hourly POS revenue',
      benchmarkSource: 'Fair Work Award + network SPLH benchmarks',
      likelihood: 78,
      runway: '2–3 weeks',
      schedule: 'Week 1: Pull 90-day hourly sales report. Week 1-2: Map roster to revenue by day-part. Week 2-3: Adjust roster, redeploy 12 hrs to high-rev slots.',
      bestChance: 'Run a 2-week trial at Prospect only — reduce Tuesday/Wednesday afternoon FTEs by 2 and add 1 FTE to Friday lunch peak. Track SPLH daily. If SPLH lifts by $5+ after 2 weeks, lock in the new roster permanently.',
    },
    {
      title: 'Upsell Combo Attachment Rate',
      description: 'Network-wide combo attachment rate is 41% vs industry best practice of 60%. Southport leads at 52%.',
      reasoning: 'Improving attachment rate by 10% across all locations could add ~$1.20 per transaction at current volumes.',
      impact: '+$18,000/month network-wide',
      priority: 'medium',
      suggestedActions: ['Roll out Southport\'s suggestive sell script to all locations', 'Add combo prompts to POS flow', 'Incentivise staff with weekly upsell leaderboard'],
      sources: ['square'],
      dataSource: 'POS itemised transaction data',
      benchmarkSource: 'QSR Media Industry Benchmarks',
      likelihood: 72,
      runway: '3–4 weeks',
      schedule: 'Week 1: Document Southport\'s suggestive sell script. Week 2: Train all location managers. Week 3: POS combo prompt config. Week 4: Launch leaderboard + weekly reviews.',
      bestChance: 'Start with the staff incentive leaderboard — visible gamification has the fastest uptake. Combine with the POS prompt (nudge + training) for compounding effect. Expect 6-8% lift in the first month, reaching target by month 3.',
    }
  ],
  default: [
    {
      title: 'Revenue Growth Opportunity',
      description: 'Analysis indicates potential to increase revenue through operational improvements.',
      reasoning: 'Cross-referencing industry benchmarks with your current performance shows areas for improvement.',
      impact: '+15% revenue potential',
      priority: 'high',
      suggestedActions: ['Review pricing strategy vs market', 'Identify top 3 underperforming areas', 'Implement customer feedback loop'],
      sources: ['xero', 'google_analytics'],
      dataSource: 'Financial data + operational metrics',
      benchmarkSource: 'ATO Small Business Benchmarks'
    },
    {
      title: 'Cost Optimisation',
      description: 'Operating costs are above industry averages in key categories.',
      reasoning: 'Supplier review and process improvement could reduce costs without impacting quality.',
      impact: '-10% in operating costs',
      priority: 'medium',
      suggestedActions: ['Conduct supplier benchmarking', 'Review top 5 expense categories', 'Identify automation opportunities'],
      sources: ['xero'],
      dataSource: 'Xero expense categorisation',
      benchmarkSource: 'Industry benchmarks via IBISWorld'
    }
  ]
};

/**
 * Get industry-specific opportunities
 * @param {string} industry - Industry identifier
 * @returns {Array} List of industry-specific opportunities
 */
export const getIndustryOpportunities = (industry) => {
  const normalizedIndustry = (industry || 'default').toLowerCase().trim();
  return INDUSTRY_OPPORTUNITIES[normalizedIndustry] || INDUSTRY_OPPORTUNITIES.default;
};

export default INDUSTRY_OPPORTUNITIES;
